import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { ArticleLayout } from "@/components/ArticleLayout";
import { getPublishedArticleBySlug } from "@/lib/publishedArticles.functions";
import { renderMarkdown } from "@/lib/markdown";
import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";

const SITE_URL = "https://coloradowebimpressions.com";

export const Route = createFileRoute("/learn/$generatedSlug")({
  loader: async ({ params }) => {
    const article = await getPublishedArticleBySlug({ data: { slug: params.generatedSlug } });
    if (!article) throw notFound();
    return { article };
  },
  head: ({ loaderData }) => {
    const a = loaderData?.article;
    if (!a) return { meta: [{ title: "Article — Colorado Web Impressions" }] };
    const url = `${SITE_URL}${a.slug}`;
    const ogImage = a.hero_image_url ?? `${SITE_URL}${ogImageFor({ articleCategory: a.category as any })}`;
    return {
      meta: [
        { title: a.meta_title ?? a.title },
        { name: "description", content: a.meta_description ?? a.excerpt },
        { property: "og:title", content: a.meta_title ?? a.title },
        { property: "og:description", content: a.meta_description ?? a.excerpt },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        ...ogMetaTags(ogImage),
        ...twitterTextTags({ title: a.title, description: a.excerpt }),
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  notFoundComponent: () => (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 p-6 text-center">
      <h1 className="text-2xl font-bold">Article not found</h1>
      <Link to="/learn" className="text-primary hover:underline">← Back to the Learning Hub</Link>
    </div>
  ),
  errorComponent: ({ error }) => {
    if (import.meta.env.DEV) console.error(error);
    return (
      <div className="p-6 text-center">
        <p className="text-destructive">We couldn't load this article — please try again.</p>
        <Link to="/learn" className="text-primary hover:underline">← Back to the Learning Hub</Link>
      </div>
    );
  },
  component: GeneratedArticlePage,
});

function GeneratedArticlePage() {
  const { article } = Route.useLoaderData();
  const html = renderMarkdown(article.body_markdown);

  return (
    <ArticleLayout
      eyebrow={article.category}
      read={`${article.read_minutes} min read`}
      title={article.title}
      intro={article.excerpt}
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Learning Hub", to: "/learn" },
        { label: article.title },
      ]}
      path={article.slug}
      datePublished={article.date_published ?? undefined}
      dateModified={article.date_updated ?? article.date_published ?? undefined}
      faqs={article.faqs as any}
    >
      {article.hero_image_url && (
        <figure className="mb-8 overflow-hidden rounded-lg">
          <img
            src={article.hero_image_url}
            alt={article.hero_image_alt ?? article.title}
            className="w-full"
            loading="eager"
          />
        </figure>
      )}

      {article.chris_take && (
        <aside className="my-8 rounded-lg border-l-4 border-primary bg-primary/5 p-5">
          <p className="mb-2 text-sm font-semibold uppercase tracking-wide text-primary">From Chris</p>
          <p className="italic leading-relaxed">{article.chris_take}</p>
        </aside>
      )}

      <div
        className="prose prose-neutral max-w-none"
        dangerouslySetInnerHTML={{ __html: html }}
      />

      <p className="mt-12 border-t pt-6 text-sm italic text-muted-foreground">
        Drafted with AI, edited and reviewed by Chris Heidlebaugh, Colorado Web Impressions.
      </p>
    </ArticleLayout>
  );
}
